import React, { useCallback, useEffect, useState } from 'react';
import {
  Table, Button, Modal, InputNumber, Input, Space, Spin, Empty, Tag, Popconfirm, notification,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import pmApi from './pmApi';
import OrgBandOverrides from './OrgBandOverrides';
import PlanBandPriceModal, { fmtMoney } from './PlanBandPriceModal';
import { Select } from 'antd';
import { PREMIUM_TYPE_ORDER, PREMIUM_TYPE_LABEL, PREMIUM_TYPE_HELP, pricingModeSummary, subBandSubtitle, groupByPremiumType } from '../../components/PlanManagement/premiumTypes';
import { Switch } from 'antd';

/**
 * Concept B — bands & pricing: the offer next to the two things it must
 * never silently touch. Price changes state their consequence; history is
 * visible; the pricing-events ledger is untouchable by design.
 *
 * This is the GENERAL / master setup (create, rename, archive sub-bands).
 * Per-plan band management also lives in PlanBandsPanel, opened from the Plans
 * Catalogue; both share PlanBandPriceModal.
 */

export default function BandsTab({ planId, plan }) {
  const [bands, setBands] = useState([]);
  const [planBands, setPlanBands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [priceModal, setPriceModal] = useState(null); // {band, current}
  const [editBand, setEditBand] = useState(null); // {id, name, premium_type, max_members, min_age, max_age, is_default_dependent}
  const [newBandOpen, setNewBandOpen] = useState(false);
  const [newBand, setNewBand] = useState({ code: '', name: '', premium_type: 'family', max_members: null, min_age: null, max_age: null });

  const load = useCallback(async () => {
    setLoading(true);
    const [b, pb] = await Promise.all([
      pmApi.bands(),
      planId ? pmApi.planBands(planId) : Promise.resolve({ ok: true, data: { bands: [] } }),
    ]);
    if (b.ok) setBands(b.data.bands || []);
    if (pb.ok) setPlanBands(pb.data.bands || []);
    setLoading(false);
  }, [planId]);

  useEffect(() => { load(); }, [load]);

  const priced = {};
  planBands.forEach((pb) => { priced[Number(pb.band_id)] = pb; });

  const openPrice = (band) => {
    const current = priced[Number(band.id)];
    setPriceModal({ band, current: current && Number(current.status) === 1 ? current : null });
  };

  const saveBandEdit = async () => {
    if (!editBand?.name) { notification.warning({ message: 'Name is required' }); return; }
    const body = { name: editBand.name };
    if (editBand.premium_type === 'dependent') {
      body.min_age = editBand.min_age ?? '';
      body.max_age = editBand.max_age ?? '';
      body.is_default_dependent = editBand.is_default_dependent ? 1 : 0;
    } else if (editBand.premium_type === 'family') {
      body.max_members = editBand.max_members;
    }
    const res = await pmApi.updateBand(editBand.id, body);
    if (res.ok) { setEditBand(null); load(); }
  };

  const createBand = async () => {
    if (!newBand.code || !newBand.name) {
      notification.warning({ message: 'Code and name are required' });
      return;
    }
    const res = await pmApi.createBand(newBand);
    if (res.ok) {
      setNewBandOpen(false);
      setNewBand({ code: '', name: '', premium_type: 'family', max_members: null, min_age: null, max_age: null });
      load();
    }
  };

  if (loading && !bands.length) return <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>;

  const columns = [
    {
      title: 'Band',
      key: 'band',
      render: (_, band) => (
        <div>
          <b>{band.name}</b>{' '}
          {Number(band.status) !== 1 ? <Tag>archived — not offered for new selection</Tag> : null}
          {Number(band.used_by_clients) > 0 ? <Tag color="blue">used by {band.used_by_clients} client{Number(band.used_by_clients) === 1 ? '' : 's'}</Tag> : null}
          <div className="pm-benefit-sub">
            {subBandSubtitle(band)}
            {Number(band.is_default_dependent) === 1 ? <> · <span className="pm-chip pm-chip-blue">default for uncategorised heads</span></> : null}
            {' · '}<code style={{ fontSize: 11 }}>{band.code}</code>
          </div>
        </div>
      ),
    },
    {
      title: planId ? `Price on ${plan?.plan_name || 'this plan'}` : 'Price',
      key: 'price',
      render: (_, band) => {
        const pb = priced[Number(band.id)];
        if (!planId) return <span className="pm-benefit-sub">pick a plan</span>;
        if (!pb || Number(pb.status) !== 1) return <span className="pm-benefit-sub">not offered</span>;
        return (
          <Space size={8}>
            <b style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(pb.effective_price ?? pb.current_price)}</b>
            {band.premium_type === 'dependent' && pb.pricing_mode && pb.pricing_mode !== 'fixed' ? (
              <span className="pm-chip pm-chip-muted">{pricingModeSummary(pb)}</span>
            ) : null}
            {Number(pb.org_override_count) > 0 ? (
              <span className="pm-chip pm-chip-blue">{pb.org_override_count} org override{pb.org_override_count > 1 ? 's' : ''}</span>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: 'History',
      key: 'history',
      render: (_, band) => {
        const pb = priced[Number(band.id)];
        const history = pb?.price_history || [];
        if (!history.length) return <span className="pm-benefit-sub">—</span>;
        return (
          <div style={{ fontSize: 11.5, color: 'var(--pm-ink-faint)' }}>
            {history.slice(0, 3).map((h, i) => (
              <div key={i} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {fmtMoney(h.price)} · {String(h.effective_from).slice(0, 10)}
                {h.effective_to ? ` → ${String(h.effective_to).slice(0, 10)}` : ' → now'}
                {' '}<span className="pm-chip pm-chip-muted">{h.source}</span>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      render: (_, band) => {
        const pb = priced[Number(band.id)];
        const isIndividual = band.code === 'individual';
        // A sub-band a client has been sold can be archived but not edited or deleted.
        const inUse = Number(band.used_by_clients) > 0;
        const deleteButton = isIndividual || inUse ? null : (
          <Popconfirm
            title="Delete this sub-band? Only possible if it was never priced or purchased — otherwise archive it."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={async () => { const r = await pmApi.deleteBand(band.id); if (r.ok) { notification.success({ message: r.message }); load(); } }}
          >
            <Button size="small" danger type="text">Delete</Button>
          </Popconfirm>
        );
        if (Number(band.status) !== 1) {
          return (
            <Space>
              <Button size="small" onClick={async () => { const r = await pmApi.updateBand(band.id, { status: 1 }); if (r.ok) { notification.success({ message: r.message }); load(); } }}>Unarchive</Button>
              {deleteButton}
            </Space>
          );
        }
        return (
          <Space>
            {inUse ? null : (
              <Button size="small" type="text" onClick={() => setEditBand({ id: band.id, name: band.name, premium_type: band.premium_type, max_members: band.max_members, min_age: band.min_age, max_age: band.max_age, is_default_dependent: Number(band.is_default_dependent) === 1 })}>Edit</Button>
            )}
            {planId ? (
            <Button size="small" onClick={() => openPrice(band)}>
              {pb && Number(pb.status) === 1 ? 'Change price…' : 'Set price…'}
            </Button>
            ) : null}
            {planId && pb && Number(pb.status) === 1 ? (
              <Popconfirm
                title="Detach band from this plan? History is retained."
                onConfirm={async () => { const r = await pmApi.detachBand(planId, band.id); if (r.ok) load(); }}
              >
                <Button size="small" danger type="text">Detach</Button>
              </Popconfirm>
            ) : null}
            {isIndividual ? null : (
              <Popconfirm
                title="Archive this sub-band? It is no longer available for new selection on any plan or purchase picker. Clients who already hold it keep it, prices are kept, and Unarchive brings it back."
                okText="Archive"
                onConfirm={async () => { const r = await pmApi.updateBand(band.id, { status: 0 }); if (r.ok) { notification.success({ message: r.message }); load(); } }}
              >
                <Button size="small" type="text">Archive</Button>
              </Popconfirm>
            )}
            {deleteButton}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 10 }}>
        <Button icon={<PlusOutlined />} onClick={() => setNewBandOpen(true)}>New sub-band</Button>
        <span className="pm-benefit-sub">Sub-bands are a shared master list under three premium types — prices belong to the Plan + sub-band pair. No price is hard-coded anywhere.</span>
      </Space>

      {bands.length ? (
        PREMIUM_TYPE_ORDER.map((type) => {
          const rows = groupByPremiumType(bands)[type];
          if (!rows.length) return null;
          return (
            <div key={type} style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                <b>{PREMIUM_TYPE_LABEL[type]}</b>
                <span className="pm-benefit-sub">{PREMIUM_TYPE_HELP[type]}</span>
              </div>
              <Table size="small" rowKey="id" columns={columns} dataSource={rows} pagination={false} />
            </div>
          );
        })
      ) : (
        <Empty description="No sub-bands yet — the migration seeds Individual, Family of 2–6 and two dependent categories" />
      )}

      {planId ? <OrgBandOverrides planId={planId} plan={plan} bands={bands} onChanged={load} /> : null}

      <PlanBandPriceModal
        planId={planId}
        target={priceModal}
        onClose={() => setPriceModal(null)}
        onSaved={load}
      />

      <Modal
        open={!!editBand}
        title={<span>Edit sub-band — {editBand?.name}</span>}
        onCancel={() => setEditBand(null)}
        onOk={saveBandEdit}
        okText="Save"
        destroyOnClose
      >
        {editBand ? (
          <Space direction="vertical" style={{ width: '100%' }} size={10}>
            <Input value={editBand.name} onChange={(e) => setEditBand((b) => ({ ...b, name: e.target.value }))} placeholder="Name" />
            {editBand.premium_type === 'family' ? (
              <InputNumber style={{ width: '100%' }} min={2} value={editBand.max_members} onChange={(v) => setEditBand((b) => ({ ...b, max_members: v }))} placeholder="Members covered" />
            ) : null}
            {editBand.premium_type === 'dependent' ? (
              <>
                <Space>
                  <InputNumber placeholder="Min age" min={0} value={editBand.min_age} onChange={(v) => setEditBand((b) => ({ ...b, min_age: v }))} />
                  <InputNumber placeholder="Max age (blank = no upper bound)" min={0} style={{ width: 240 }} value={editBand.max_age} onChange={(v) => setEditBand((b) => ({ ...b, max_age: v }))} />
                </Space>
                <Space>
                  <Switch checked={!!editBand.is_default_dependent} onChange={(v) => setEditBand((b) => ({ ...b, is_default_dependent: v }))} />
                  <span className="pm-benefit-sub">Default category for policies that only recorded a number of additional heads (one category holds this)</span>
                </Space>
              </>
            ) : null}
            <span className="pm-benefit-sub">Age ranges and the default category are configuration — nothing about dependents is fixed in code.</span>
          </Space>
        ) : null}
      </Modal>

      <Modal
        open={newBandOpen}
        title="New sub-band (shared master list)"
        onCancel={() => setNewBandOpen(false)}
        onOk={createBand}
        okText="Create"
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size={10}>
          <Select
            style={{ width: '100%' }}
            value={newBand.premium_type}
            onChange={(v) => setNewBand((s) => ({ ...s, premium_type: v, max_members: v === 'individual' ? 1 : s.max_members }))}
            options={PREMIUM_TYPE_ORDER.map((t) => ({ value: t, label: PREMIUM_TYPE_LABEL[t] }))}
          />
          <Input
            placeholder={newBand.premium_type === 'dependent' ? 'Code (e.g. dependent_under_18)' : 'Code (e.g. family_4)'}
            value={newBand.code}
            onChange={(e) => setNewBand((s) => ({ ...s, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }))}
          />
          <Input
            placeholder={newBand.premium_type === 'dependent' ? 'Name (e.g. Dependent under 18)' : 'Name (e.g. Family of 4)'}
            value={newBand.name}
            onChange={(e) => setNewBand((s) => ({ ...s, name: e.target.value }))}
          />
          {newBand.premium_type === 'family' ? (
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Members covered (e.g. 4)"
              min={2}
              value={newBand.max_members}
              onChange={(v) => setNewBand((s) => ({ ...s, max_members: v }))}
            />
          ) : null}
          {newBand.premium_type === 'dependent' ? (
            <Space>
              <InputNumber placeholder="Min age" min={0} value={newBand.min_age} onChange={(v) => setNewBand((s) => ({ ...s, min_age: v }))} />
              <InputNumber placeholder="Max age (blank = no upper bound)" min={0} style={{ width: 240 }} value={newBand.max_age} onChange={(v) => setNewBand((s) => ({ ...s, max_age: v }))} />
            </Space>
          ) : null}
          <span className="pm-benefit-sub">Sub-bands carry no price here — pricing is a per-plan act. Dependent categories are offered on every plan that prices them.</span>
        </Space>
      </Modal>
    </div>
  );
}
